<template>
    <div :class="$style.form">
        <pt-form
            ref="form"
            label-position="top"
            :model="formData"
            :rules="rules"
            @submit.prevent="createHandler"
        >
            <!-- <pt-form-item :label="inputLable" prop="name" :error="nameError">
                <pt-input
                    ref="nameInput"
                    v-model="formData.name"
                    :placeholder="t('experience.common.experience_name_placeholder')"
                    :maxlength="constantConfig.EXPERIENCE_NAME_MAX_LENGTH"
                    tabindex="1"
                    :autofocus="true"
                    @focus="handleClearError"
                ></pt-input>
            </pt-form-item> -->
            <pt-form-item
                prop="url"
                :label="$t('experience.setting.input_url_label')"
                v-if="hasBaseUrl"
            >
                <pt-autocomplete
                    :maxlength="constantConfig?.EXPERIENCE_BASE_URL_MAX_LENGTH || 600"
                    :placeholder="$t('experience.common.experience_input_url_placeholder')"
                    :class="$style.autocomplete"
                    v-model="formData.url"
                    :autofocus="true"
                    :trim-space="true"
                    :fetch-suggestions="querySearch"
                    @focus="clearVlidateError('url')"
                    @blur="validateForm"
                    @select="validateForm"
                ></pt-autocomplete>
            </pt-form-item>
        </pt-form>

        <pt-button
            size="medium"
            color="darkgreen"
            fullfilled
            @click="createHandler"
            :loading="createLoading"
            :disabled="disabled"
        >
            {{ $t('experience.common.create_now') }}
        </pt-button>
    </div>
</template>

<script lang="ts">
import ExperienceDialog from './ExperienceDialog.vue';

export default {
    name: 'ExperienceForm',
    components: {
        ExperienceDialog
    }
};
</script>

<script lang="ts" setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import isUrl from 'validator/lib/isURL';
import constantConfig from '@experience/config/constant.config';
import { useTopMetrics } from '@models/experience/useTopMetrics';
// import { useProfileInfo } from '@models/profile/useCurrentProfile';
// import { useAllUserGroups } from '@models/experience/useUserGroups';
import { useExperience, ExperienceType } from '@models/experience/experience';
import type { TypeItem } from '@experience/static/config/experience.type';
import {
    useCreateDataStore,
    EntryPosition,
    ComponentData
} from '@experience/store/experience.create.store';
import { useConditionsStore } from '@experience/store/experience.conditions.store';

interface Props {
    entryItem: TypeItem;
    entryPosition: EntryPosition;
    experienceType: ExperienceType;
    data: ComponentData;
}

// props
const props = defineProps<Props>();

// emits
const emit = defineEmits(['close', 'prev', 'update', 'submit']);

// state
// const route = useRoute();
// const router = useRouter();
const conditionsStore = useConditionsStore();
const { t } = useI18n();
const createDataStore = useCreateDataStore();
// const { profileInfo } = useProfileInfo();
const { experienceInfo, createExperience } = useExperience();
const { querySearch } = useTopMetrics();
// const { data: allUsergroups } = useAllUserGroups();
const form = ref();
// const nameInput = ref();
// const nameError = ref('');
const hasBaseUrl = ['INLINE', 'ADVANCED'].includes(props.experienceType || '');
const formData = reactive<{ name?: string; url?: string }>({
    url: ''
});
const validateUrl = (rule: any, value: string, callback: (arg0?: Error | undefined) => void) => {
    if (!value) {
        callback(new Error(t('experience.common.experience_input_url_placeholder')));
    } else {
        if (isUrl(value)) {
            callback();
        } else {
            callback(new Error(t('experience.setting.url_valide_error')));
        }
    }
};
const rules = {
    // name: [
    //     {
    //         required: true,
    //         message: t('experience.common.experience_name_placeholder'),
    //         trigger: 'blur'
    //     }
    // ],
    url: [
        {
            required: true,
            message: t('experience.common.experience_input_url_placeholder'),
            trigger: 'blur'
        },
        {
            validator: validateUrl,
            trigger: ['select', 'blur']
        }
    ]
};

// computed
// const inputLable = computed(() => {
//     switch (props.entryItem?.key) {
//         case 'ABTEST':
//             return t('experience.common.create_dialog_input_label_abtest');
//         case 'REDIRECT':
//             return t('experience.common.create_dialog_input_label_redirect');
//         default:
//             return t('experience.common.create_dialog_input_label');
//     }
// });

const disabled = computed(() => {
    return Object.keys(formData).some(key => {
        const val = formData[key as 'url' | 'name'];
        return !val || (key === 'url' && !isUrl(val));
    });
});

// const commonUsergroupId = computed(() => {
//     return allUsergroups.value?.find(usrg => usrg.source === 'default')?.id;
// });

const createLoading = computed(() => {
    return createDataStore.createLoading;
});

onMounted(() => {
    if (hasBaseUrl) {
        formData.url = '';
        const { url } = conditionsStore.pageConditions || createDataStore.experienceCreateData.pageCondition || {};
        if (url) {
            formData.url = url;
        }
    }
});

watch(
    () => experienceInfo.value,
    () => {
        if (experienceInfo.value) {
            formData.url = experienceInfo.value?.baseURL;
        }
    }
);

function clearVlidateError(propsName: string) {
    form.value?.clearValidate(propsName);
}

function validateForm(item: any) {
    form.value?.validate();
}

async function createHandler() {
    form.value &&
        form.value.validate(async (valide: boolean) => {
            if (!valide) return;
            createDataStore.updateExperienceCreateData({
                url: formData.url as string
            });
            await createDataStore.createExperienceHandler();
        });
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

.form {
    max-width: 468px;
    margin: 0 auto;

    :global {
        .pt-form-item__label {
            line-height: 1em;
            padding-bottom: 12px !important;
        }
        .pt-form-item.is-required:not(.is-no-asterisk) > .pt-form-item__label:before {
            content: '';
        }
    }

    .autocomplete {
        display: block;
    }

    form {
        margin-bottom: 48px;
    }
}
</style>
